import React, { useEffect, useState, useContext } from 'react';
import DatahubAPI from '../../api/DatahubAPI';
import CircularProgress from '@mui/material/CircularProgress';
import HitBreakdown from '../../components/Watchlist/HitBreakdown';
import WatchlistUpper from 'components/Watchlist/WatchlistUpper';
import WatchlistForm from 'components/Watchlist/WatchlistForm';
import EntityResolution from '../../components/Common/EntityResolution';
import { exportToCSV } from 'components/Common/CSVManage';
import { useSnackbar } from 'notistack';
import Modal from 'react-modal';
import { Close } from '@mui/icons-material';
import { Typography } from '@mui/material';
import {
  StyledInput,
  NextButton,
} from '../../components/Common/MUIStyledComponents';
import { Redirect } from 'react-router-dom';
import { GlobalContext } from 'providers/GlobalProvider';
import SubscribeModal from 'components/SubscribeModal';
import { Player } from '@lottiefiles/react-lottie-player';
import ModuleHeaderLayout from 'components/Common/Layouts/ModuleHeaderLayout';
import { ModuleContentTooltip } from '../../components/Common/MUIStyledComponents';

const WatchlistReact: React.FC = () => {
  // PAYMENTS STATE AND FUNCTIONS
  const { cur_user } = useContext(GlobalContext);
  const [ userHasWatchlist, setUserHasWatchlist ] = useState(false);
  const [ matchedTeamWithStripe, setMatchedTeamWithStripe ] =
    useState<string>('');
  const [ activeSub, setActiveSub ] = useState(false);
  const [ selectedIndex, setSelectedIndex ] = useState<string>('Watchlist');

  const handleIndexChange = (index: string) => {
    switch (index) {
    case 'Watchlist':
      setSelectedIndex('Watchlist');
      break;
    case 'Country Risk':
      setSelectedIndex('Country Risk');
      break;
    default:
      setSelectedIndex('Watchlist');
    }
  };

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  const checkForWatchlist = () => {
    if (
      cur_user.selected_team?.attributes?.tier?.includes('enterprise') ||
      cur_user.selected_team?.attributes?.tier?.includes('standard') ||
      cur_user.selected_team?.attributes?.tier?.includes('pro')
    ) {
      setUserHasWatchlist(true);
    }
    if (cur_user.stripe_cus_info) {
      cur_user.stripe_cus_info.map((cus: any) => {
        if (cus.name === cur_user.selected_team.name) {
          setMatchedTeamWithStripe(cus.id);
          cus.subscriptions.data.map((sub: any) => {
            if (
              sub.metadata.moduleName === 'BITE Standard' ||
              sub.metadata.moduleName === 'BITE Pro' ||
              sub.metadata.moduleName === 'BITE Enterprise'
            ) {
              setActiveSub(true);
            }
          });
        }
      });
    }
  };

  useEffect(() => {
    checkForWatchlist();
  }, [ activeSub ]);

  const [ countryData, setCountryData ] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: [] });
  const [ tableData, setTableData ] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [ watchlistPrimaryOpts, setWatchlistPrimaryOpts ] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [ countryOpts, setCountryOpts ] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [ sourceCountryOpts, setSourceCountryOpts ] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [ agencyOpts, setAgencyOpts ] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [ riskAgencyOpts, setRiskAgencyOpts ] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [ listOpts, setListOpts ] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [ riskListOpts, setRiskListOpts ] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [ pageNum, setPageNum ] = useState<number>(0);
  const [ entityToggle, setEntityToggle ] = useState(false);
  const [ fileName, setFileName ] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [ fileNameModalOpen, setFileNameModalOpen ] = useState(false);

  interface IFormValues {
    searchValue: string;
    country: string[];
    govtAgency: string[];
    listName: string[];
    sourceCountry: string[];
    riskGovtAgency: string[];
    riskListName: string[];
    fuzziness: number;
  }

  // FORM VALUES
  const [ formValues, setFormValues ] = useState<IFormValues>({
    searchValue: '',
    country: [],
    sourceCountry: [],
    govtAgency: [],
    riskGovtAgency: [],
    listName: [],
    riskListName: [],
    fuzziness: 0,
  });

  // file modal
  const handleFileNameChange = (e: any) => {
    setFileName(e.target.value);
  };

  const handleFileNameClose = () => {
    setFileNameModalOpen(false);
    setFileName('');
  };

  const toggleModal = () => {
    setFileNameModalOpen(true);
  };

  const handleScroll = () => {
    document
      .getElementById('scroll-id')
      ?.scrollIntoView({ behavior: 'smooth' });
  };

  const wlHeaders = [
    'Name of Entity',
    'Program',
    'Source Country',
    'Agency',
    'List Name',
    'Additional Remarks',
  ];
  const countryRiskHeaders = [ 'Source Country, List Name, Additional Remarks' ];

  const handleCSV = () => {
    if (selectedIndex === 'Watchlist') {
      if (tableData.data.hits.hits.length >= 1) {
        exportToCSV(
          fileName,
          [ ...tableData.data.hits.hits ],
          wlHeaders,
          'watchlist'
        );
      } else {
        enqueueSnackbar('No Result Data to Export', { variant: 'error' });
      }
    }
    if (selectedIndex === 'Country Risk') {
      if (tableData.data.hits.hits.length >= 1) {
        exportToCSV(
          fileName,
          [ ...tableData.data.hits.hits ],
          countryRiskHeaders,
          'countryRisk'
        );
      } else {
        enqueueSnackbar('No Result Data to Export', { variant: 'error' });
      }
    }
  };

  const getInitialResults = async() => {
    const res = await DatahubAPI.watchlist({
      agg: true,
      pageNum: 0,
      indexName: selectedIndex,
    });
    if (res?.success) {
      setTableData({ isLoading: false, data: res?.result?.result?.body });
      setCountryData({
        isLoading: false,
        data: res?.result?.result?.body?.aggregations,
      });
      setWatchlistPrimaryOpts({
        isLoading: false,
        data: res?.result?.result?.body?.aggregations,
      });
    }
  };

  const queryResults = async(values: any, page: number) => {
    let fuzzyValue = '';
    if (entityToggle) {
      fuzzyValue = 'AUTO';
    } else {
      fuzzyValue = '0';
    }
    const res = await DatahubAPI.watchlist({
      primary: values.searchValue,
      indexName: selectedIndex,
      listCountry: values.country,
      sourceCountry: values.sourceCountry,
      govtAgency: values.govtAgency,
      riskGovtAgency: values.riskGovtAgency,
      listName: values.listName,
      riskListName: values.riskListName,
      fuzziness: fuzzyValue,
      pageNum: page,
      agg: true,
    });
    if (res?.success) {
      setCountryData({
        isLoading: false,
        data: res?.result?.result?.body?.aggregations,
      });
      setTableData({ isLoading: false, data: res?.result?.result?.body });
    } else {
      enqueueSnackbar('Error retrieving data. Restricted Parties may be being updated', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    }
  };

  useEffect(() => {
    setFormValues({
      searchValue: '',
      country: [],
      sourceCountry: [],
      listName: [],
      riskListName: [],
      govtAgency: [],
      riskGovtAgency: [],
      fuzziness: 0,
    });
    getInitialResults();
  }, [ selectedIndex ]);

  // dynamic table data filtering on dropdown option select/deselect
  useEffect(() => {
    queryResults(formValues, pageNum);
  }, [
    formValues.country,
    formValues.govtAgency,
    formValues.listName,
    formValues.sourceCountry,
    formValues.riskGovtAgency,
    formValues.riskListName,
  ]);

  const helpContent = (
    <p className='module-header-tooltip-text'>
      The Restricted Parties serves as a database where you can search all
      internationally flagged entities. Use the search bar to look up specific
      queries. Use Advanced Filters to select specific Sanctioning Countries and
      Lists.
    </p>
  );

  return (
    <ModuleHeaderLayout title='Restricted Parties' helpContent={helpContent}>
      {activeSub && !userHasWatchlist ? (
        <div className='no-license-modal'>
          <Player
            autoplay
            loop
            src='https://lottie.host/3c23c1ad-7f71-4e3d-8f19-69154715dbdc/SdWWjCmU1J.json'
            style={{ width: '20%' }}
          ></Player>
          <Typography
            variant='h6'
            className='text-white'
            style={{
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.15px',
            }}
          >
            You need an assigned license to access this module
          </Typography>
          <Typography
            variant='h6'
            style={{
              fontSize: '18px',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              marginTop: '20px',
              color: '#BDBEC1',
            }}
          >
            Visit User Management &gt; Add License &gt; to assign an available
            module license to your user or contact your team admin
          </Typography>
        </div>
      ) : !userHasWatchlist ? (
        <SubscribeModal
          name='Restricted Parties'
          description='More than 300+ screening lists available for search and analysis'
          price={25}
          curUser={cur_user}
          stripeTeam={matchedTeamWithStripe}
          activeSub={activeSub}
        />
      ) : (
        <></>
      )}
      <div className={`os-iframe${!userHasWatchlist ? '-blur' : ''}`}>
        <div className='bl-index-select'>
          <div
            className='bl-index-item'
            onClick={() => handleIndexChange('Watchlist')}
          >
            <ModuleContentTooltip
              placement='top'
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    'display': 'flex',
                    'padding': 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                    'flexDirection': 'column',
                    'alignItems': 'flex-start',
                    'alignSelf': 'stretch',
                    'borderRadius': 'var(--radius-md, 8px)',
                    'background': 'var(--Background-200, #202227)',
                    'maxWidth': '400px',
                    '& .MuiTooltip-arrow': {
                      color: 'var(--Background-200, #202227)',
                    },
                  },
                },
              }}
              title={
                <p className='module-header-tooltip-text'>
                  Use the search bar to look up specific queries (entities or
                  addresses). Use Advanced Filters to select specific Listing
                  Countries and List Names.
                </p>
              }
            >
              <div className='bl-item-title'>Watchlist</div>
            </ModuleContentTooltip>
            <div className='bl-item-svg'>
              {selectedIndex === 'Watchlist' ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='206'
                  height='4'
                  viewBox='0 0 206 4'
                  fill='none'
                >
                  <path
                    d='M2 2L204.333 1.99998'
                    stroke='url(#paint0_linear_2173_3230)'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                  <defs>
                    <linearGradient
                      id='paint0_linear_2173_3230'
                      x1='2'
                      y1='2'
                      x2='204.333'
                      y2='1.99977'
                      gradientUnits='userSpaceOnUse'
                    >
                      <stop stopColor='#4CC9F0' />
                      <stop offset='0.453125' stopColor='#4895EF' />
                      <stop offset='1' stopColor='#4361EE' />
                    </linearGradient>
                  </defs>
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='207'
                  height='4'
                  viewBox='0 0 207 4'
                  fill='none'
                >
                  <path
                    d='M2.33337 2L204.667 2.00002'
                    stroke='#4A4C51'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                </svg>
              )}
            </div>
          </div>
          <div
            className='bl-index-item'
            onClick={() => handleIndexChange('Country Risk')}
          >
            <ModuleContentTooltip
              placement='top'
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    'display': 'flex',
                    'padding': 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                    'flexDirection': 'column',
                    'alignItems': 'flex-start',
                    'alignSelf': 'stretch',
                    'borderRadius': 'var(--radius-md, 8px)',
                    'background': 'var(--Background-200, #202227)',
                    'maxWidth': '400px',
                    '& .MuiTooltip-arrow': {
                      color: 'var(--Background-200, #202227)',
                    },
                  },
                },
              }}
              title={
                <p className='module-header-tooltip-text'>
                  Use the search bar to look up specific queries (entities or
                  addresses). Use Advanced Filters to select specific Listing
                  Countries and List Names.
                </p>
              }
            >
              <div className='bl-item-title'>Country Risk</div>
            </ModuleContentTooltip>
            <div className='bl-item-svg'>
              {selectedIndex === 'Country Risk' ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='206'
                  height='4'
                  viewBox='0 0 206 4'
                  fill='none'
                >
                  <path
                    d='M2 2L204.333 1.99998'
                    stroke='url(#paint0_linear_2173_3230)'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                  <defs>
                    <linearGradient
                      id='paint0_linear_2173_3230'
                      x1='2'
                      y1='2'
                      x2='204.333'
                      y2='1.99977'
                      gradientUnits='userSpaceOnUse'
                    >
                      <stop stopColor='#4CC9F0' />
                      <stop offset='0.453125' stopColor='#4895EF' />
                      <stop offset='1' stopColor='#4361EE' />
                    </linearGradient>
                  </defs>
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='207'
                  height='4'
                  viewBox='0 0 207 4'
                  fill='none'
                >
                  <path
                    d='M2.33337 2L204.667 2.00002'
                    stroke='#4A4C51'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                </svg>
              )}
            </div>
          </div>
        </div>
        <WatchlistForm
          selectedIndex={selectedIndex}
          formValues={formValues}
          setFormValues={setFormValues}
          setWatchlistPrimaryOpts={setWatchlistPrimaryOpts}
          watchlistPrimaryOpts={watchlistPrimaryOpts}
          countryOpts={countryOpts}
          setCountryOpts={setCountryOpts}
          sourceCountryOpts={sourceCountryOpts}
          setSourceCountryOpts={setSourceCountryOpts}
          agencyOpts={agencyOpts}
          setAgencyOpts={setAgencyOpts}
          riskAgencyOpts={riskAgencyOpts}
          setRiskAgencyOpts={setRiskAgencyOpts}
          listOpts={listOpts}
          setListOpts={setListOpts}
          riskListOpts={riskListOpts}
          setRiskListOpts={setRiskListOpts}
          getInitialResults={getInitialResults}
          queryResults={queryResults}
          pageNum={pageNum}
        />
        <EntityResolution
          entityToggle={entityToggle}
          setEntityToggle={setEntityToggle}
        />
        <div className='wl-upper-results'>
          {countryData.isLoading ? (
            <div>
              <CircularProgress style={{ color: '#0E8BFF' }} />
            </div>
          ) : (
            <WatchlistUpper
              selectedIndex={selectedIndex}
              handleScroll={handleScroll}
              countryData={countryData}
            />
          )}
        </div>
        {tableData.isLoading ? (
          <div>
            <CircularProgress style={{ color: '#0E8BFF' }} />
          </div>
        ) : (
          <div id='scroll-id'>
            <HitBreakdown
              selectedIndex={selectedIndex}
              toggleModal={toggleModal}
              queryResults={queryResults}
              formValues={formValues}
              pageNum={pageNum}
              setPageNum={setPageNum}
              tableData={tableData}
            />
          </div>
        )}
        <Modal
          isOpen={fileNameModalOpen}
          onRequestClose={handleFileNameClose}
          ariaHideApp={false}
        >
          <div className='bite-score-file-modal'>
            <div className='display-flex justify-between width-100'>
              <Typography variant='h5'>File Name:</Typography>
              <Close onClick={handleFileNameClose} className='cursor-pointer' />
            </div>
            <div className='display-flex justify-end width-100'>
              <StyledInput
                value={fileName}
                onChange={handleFileNameChange}
                variant='outlined'
                className='margin-right-20px'
              />
              <NextButton
                onClick={() => {
                  handleCSV();
                  handleFileNameClose();
                }}
              >
                Download
              </NextButton>
            </div>
          </div>
        </Modal>
      </div>
    </ModuleHeaderLayout>
  );
};

export default WatchlistReact;
